import { createSelector } from '@reduxjs/toolkit';
import { SiteTypePath, SiteTypes, UniqueIdentifier } from '../../../types';

export type TenantsConfig = {
  [key: string]: {
    domain: string;
    useWWW: boolean;
    singleSiteMode: boolean;
  };
};

type ContentConfig = {
  terms: { termsAndConditionsUrl: string };
};

export type TenantConfig = {
  outputName: string;
  contentConfig: ContentConfig;
  domain: string;
  brandHubId: string;
  copyright: string;
  gtmContainerIds: string[];
  robotsSettings?: string;
  transcendPrivacyPolicyUrl?: string;
};

export type ElementsProperties = {
  [key: string]: {
    height: number;
  };
};

// Available portfolio ids in our database, will be removed soon
// From AUTOMOTIVE_KNECT365 everything be always redirected on informaconnect.com
// On the admin UI you can still see AUTOMOTIVE_KNECT365 portfolio
export enum PortfolioId {
  AUTOMOTIVE_KNECT365 = 'automotive.knect365.com',
  TMT_KNECT365 = 'tmt.knect365.com',
  TELECOMSTECHACADEMY_KNECT365 = 'telecomstechacademy.knect365.com',
}

// The list of tenants domains that used as IDs. This IDs used across whole app
// The ful list you can find here:
// https://caas-configuration.eks-internal.knnlab.com/KT-2469-tenants-structure-modification/core-tenants-default.json
export enum TenantDomainId {
  INFORMACONNECT = 'informaconnect.com',
  KNECT365 = 'knect365.com',
  TMTKNECT365 = 'tmt.knect365.com',
  TELECOMSTECHACADEMY = 'telecomstechacademy.knect365.com',
  AUTOMOTIVE = 'automotive.knect365.com',
  FANEXPOHQ = 'fanexpohq.com',
  CONNECTECHASIA = 'connectechasia.com',
  LONDONTECHWEEK = 'events.londontechweek.com',
  ASIATECHXSINGAPORE = 'asiatechxsg.com',
  ONEOFAKINDSHOW = 'oneofakindshow.com',
  INTERIORDESIGNSHOW = 'interiordesignshow.com',
  ARTTORONTO = 'arttoronto.ca',
  THEARTISTSPROJECT = 'theartistproject.com',
  MIDDLEEASTFILMCOMICCON = 'mefcc.com',
  NETWORKXEVENT = 'networkxevent.com',
  AFRICATECHFESTIVAL = 'africatechfestival.com',
  EXPOTENANT = 'expotenant.com',
  IWCE = 'iwceexpo.com',
  EQLHER = 'eql-her.com',
  GAMEDEVELOPERSCONFERENCE = 'gdconf.com',
  ENTERPRISECONNECT = 'enterpriseconnect.com',
  NATIONALRESTAURANTSHOW = 'nationalrestaurantshow.com',
  NEWYORKFARMSHOW = 'newyorkfarmshow.com',
  AILEADERSFORUM = 'ai-leadersforum.com',
  CLIMATELEADERSFORUM = 'climate-leaders-forum.com',
  MSPSUMMIT = 'themspsummit.com',
  SERVICEMANAGEMENTWORLD = 'smworld.com',
  CHANNELPARTNERS = 'channelpartnersconference.com',
  DATACENTERWORLD = 'datacenterworld.com',
  DCWPOWER = 'dcwpower.com',
  TECHNOMIC = 'technomic.com',
  MARKETSSAMPLESITE = 'informa-markets-sample.com',
  AUTOTECHEVENTS = 'autotechevents.com',
  MENUMASTERS = 'menumasters.com',
  NETWORKXSERIES = 'networkxseries.com',
  MENUMASTERSEVENT = 'menumastersevent.com',
  PROJECTENCORECOMMS = 'projectencore.informa.com',
  OMDIAEVENTS = 'omdiaevents.com',
  GLOBALLONGEVITYSUMMIT = 'globallongevitysummit.com',
  GAMEDEVELOPERSCHOICEAWARDS = 'gamechoiceawards.com',
  INDEPENDENTGAMESFESTIVAL = 'igf.com',
  CULINARYEXPERIENCE = 'culinaryexperience.vip',
  THEWASTESUMMIT = 'thewastesummit.com',
  LTW365 = 'ltw365.com',
  AIINRETAIL = 'aiinretail.co.uk',
  CIOSUMMIT = 'cio-summit.co.uk',
  CDAOSUMMIT = 'cdaosummit.com',
  AIINFINANCE = 'aiinfinance.co.uk',
  SMHSCHOOLS = 'smhschools.com.au',
  IND_GROUP = 'ind-group.com',
}

export interface PageConfig {
  siteId: UniqueIdentifier;
  sitePath: string;
  siteSubPath?: string;
  siteTypePath: SiteTypePath;
  canonicalUrl: string;
  parentDomain: string;
  tenantId: TenantDomainId;
  portfolioId: PortfolioId;
  portfolioName: string;
  homeOrigin: string;
  siteArticlePath: string;
  tenantsConfig: TenantsConfig;
  tenantConfig: TenantConfig;
  cookieSitePath: string;
  location: Location;
  siteType: SiteTypes;
  elementsProperties: ElementsProperties;
  singleSiteMode: boolean;
  ipAddress: string;
}

export const selectPageConfig = createSelector(
  [(state) => state.pageConfig as PageConfig],
  (data) => data,
);
